import { useEffect, useMemo, useState, type JSX } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { teamLabel } from '@/lib/labels';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useOutreachInviters, useOutreachTeams } from '../hooks/bridge';
import type { CreatorListFilters, LeadFilter, ReassignBody } from '../api/types';
import { useExportCreators, useExportFolder, useReassignCreators, useReassignFolder } from './hooks';
import { exportSummaryText, reassignSummaryText } from './exportSummary';
import { CheckboxField, Note, Segmented } from './ui';

/**
 * Who a bulk action is aimed at. `rows` carries explicit ids; `all` means
 * "everything matching this filter", including rows that were never loaded.
 */
export type BulkTarget =
  | { kind: 'rows'; count: number; usernames: string[]; leadIds: number[]; creatorIds: number[] }
  | { kind: 'all'; count: number; filter: LeadFilter | CreatorListFilters };

export type BulkScope = { kind: 'folder'; folderPublicId: string } | { kind: 'creators' };

type AssignMode = 'pool' | 'inviter' | 'team' | 'unassign';

function useAssigneeOptions() {
  const inviters = useOutreachInviters();
  const teams = useOutreachTeams();
  // Show team names, never raw codes like SOCIAL_MEDIA.
  const teamName = (code: string | null | undefined): string | undefined =>
    code ? teamLabel(code, (teams.data ?? []).find((t) => t.code === code)?.displayName) : undefined;
  const inviterOptions = useMemo(
    () =>
      (inviters.data ?? [])
        .filter((i) => i.isActive)
        .map((i) => ({ value: String(i.id), label: i.name, hint: teamName(i.team) })),
    [inviters.data, teams.data],
  );
  const teamOptions = useMemo(
    () => (teams.data ?? []).filter((t) => t.isActive).map((t) => ({ value: t.code, label: t.displayName })),
    [teams.data],
  );
  return { inviterOptions, teamOptions, loading: inviters.isLoading || teams.isLoading };
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

// ── Send to outreach ───────────────────────────────────────────────────────

/**
 * Sends leads to the outreach lead queue. A selection goes as explicit ids;
 * with nothing selected the whole filtered set goes as `all: true` + the
 * filter, and only after an explicit "Send all N leads matching your filters"
 * tick — the old dialog sent everything the moment nothing was selected.
 */
export function SendToOutreachDialog({
  open,
  onOpenChange,
  scope,
  target,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: BulkScope;
  target: BulkTarget;
  onDone?: () => void;
}): JSX.Element {
  const { inviterOptions, teamOptions, loading } = useAssigneeOptions();
  const folderExport = useExportFolder(scope.kind === 'folder' ? scope.folderPublicId : '');
  const creatorsExport = useExportCreators();
  const mutation = scope.kind === 'folder' ? folderExport : creatorsExport;

  const [mode, setMode] = useState<AssignMode>('pool');
  const [inviterId, setInviterId] = useState('');
  const [team, setTeam] = useState('');
  const [reassignExisting, setReassignExisting] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode('pool');
    setInviterId('');
    setTeam('');
    setReassignExisting(false);
    setConfirmAll(false);
    mutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isAll = target.kind === 'all';
  const canSubmit =
    target.count > 0 &&
    !mutation.isPending &&
    (!isAll || confirmAll) &&
    (mode === 'pool' || (mode === 'inviter' && !!inviterId) || (mode === 'team' && !!team));

  function submit() {
    const assign = {
      ...(mode === 'inviter' && inviterId ? { assignToInviterId: Number(inviterId) } : {}),
      ...(mode === 'team' && team ? { assignToTeam: team } : {}),
      ...(reassignExisting && mode !== 'pool' ? { reassignExisting: true } : {}),
    };
    const onSuccess = (response: Parameters<typeof exportSummaryText>[0]) => {
      const { title, detail } = exportSummaryText(response);
      toast.success(title, detail ? { description: detail } : undefined);
      onOpenChange(false);
      onDone?.();
    };
    if (scope.kind === 'folder') {
      folderExport.mutate(
        target.kind === 'all'
          ? { all: true, filter: target.filter as LeadFilter, ...assign }
          : { leadIds: target.leadIds, ...assign },
        { onSuccess },
      );
    } else {
      creatorsExport.mutate(
        target.kind === 'all'
          ? { all: true, filter: target.filter as CreatorListFilters, ...assign }
          : { creatorIds: target.creatorIds, ...assign },
        { onSuccess },
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send {plural(target.count, 'lead')} to outreach</DialogTitle>
          <DialogDescription>
            {isAll
              ? 'Nothing is selected, so this sends every lead that matches your current filters.'
              : 'They join the outreach lead queue. Leads already there are left as they are.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>Assign to</Label>
            <Segmented
              label="Assign to"
              value={mode}
              onChange={setMode}
              options={[
                { value: 'pool', label: 'Everyone' },
                { value: 'inviter', label: 'Outreacher' },
                { value: 'team', label: 'Team' },
              ]}
            />
          </div>
          {mode === 'pool' && (
            <p className="text-sm text-muted-foreground">Leads go to the shared lead queue, open to every outreacher.</p>
          )}
          {mode === 'inviter' && (
            <Combobox
              value={inviterId}
              onChange={setInviterId}
              options={inviterOptions}
              placeholder="Search outreachers…"
              emptyText="No outreachers found"
              loading={loading}
            />
          )}
          {mode === 'team' && (
            <Combobox
              value={team}
              onChange={setTeam}
              options={teamOptions}
              placeholder="Pick a team…"
              emptyText="No teams found"
              loading={loading}
            />
          )}
          {mode !== 'pool' && (
            <CheckboxField
              checked={reassignExisting}
              onChange={setReassignExisting}
              hint="Without this, leads already in outreach keep whoever they're assigned to now."
            >
              Also update who it's assigned to for leads already in outreach
            </CheckboxField>
          )}
          {isAll && (
            <div className="rounded-lg border border-border p-3">
              <CheckboxField checked={confirmAll} onChange={setConfirmAll}>
                Send all {plural(target.count, 'lead')} matching your filters
              </CheckboxField>
            </div>
          )}
          {mutation.error && <Note tone="error">{(mutation.error as Error).message}</Note>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {mutation.isPending ? 'Sending…' : `Send ${plural(target.count, 'lead')}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Reassign ────────────────────────────────────────────────────────────────

/**
 * Changes who leads already in outreach are assigned to. Leads that were
 * never sent are counted back as "not in outreach" rather than silently
 * ignored.
 */
export function ReassignDialog({
  open,
  onOpenChange,
  scope,
  target,
  title,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: BulkScope;
  target: BulkTarget;
  title?: string;
  onDone?: () => void;
}): JSX.Element {
  const { inviterOptions, teamOptions, loading } = useAssigneeOptions();
  const folderReassign = useReassignFolder(scope.kind === 'folder' ? scope.folderPublicId : '');
  const creatorsReassign = useReassignCreators();
  const mutation = scope.kind === 'folder' ? folderReassign : creatorsReassign;

  const [mode, setMode] = useState<Exclude<AssignMode, 'pool'>>('inviter');
  const [inviterId, setInviterId] = useState('');
  const [team, setTeam] = useState('');
  const [releaseClaims, setReleaseClaims] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode('inviter');
    setInviterId('');
    setTeam('');
    setReleaseClaims(false);
    mutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const canSubmit =
    target.count > 0 &&
    !mutation.isPending &&
    (mode === 'unassign' || (mode === 'inviter' && !!inviterId) || (mode === 'team' && !!team));

  function submit() {
    const assign: Pick<ReassignBody, 'assignToInviterId' | 'assignToTeam'> =
      mode === 'inviter'
        ? { assignToInviterId: Number(inviterId) }
        : mode === 'team'
          ? { assignToInviterId: null, assignToTeam: team }
          : { assignToInviterId: null, assignToTeam: null };
    const who =
      target.kind === 'all'
        ? { all: true, filter: target.filter }
        : scope.kind === 'folder'
          ? { usernames: target.usernames }
          : { creatorIds: target.creatorIds };
    const body: ReassignBody = { ...who, ...assign, releaseClaims };
    const onSuccess = (r: Parameters<typeof reassignSummaryText>[0]) => {
      const { title: t, detail } = reassignSummaryText(r);
      toast.success(t, detail ? { description: detail } : undefined);
      onOpenChange(false);
      onDone?.();
    };
    if (scope.kind === 'folder') folderReassign.mutate(body, { onSuccess });
    else creatorsReassign.mutate(body, { onSuccess });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title ?? `Reassign ${plural(target.count, 'lead')}`}</DialogTitle>
          <DialogDescription>
            Changes who these leads are assigned to in outreach. Leads that haven't been sent yet are skipped.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>Assign to</Label>
            <Segmented
              label="Assign to"
              value={mode}
              onChange={setMode}
              options={[
                { value: 'inviter', label: 'Outreacher' },
                { value: 'team', label: 'Anyone on a team' },
                { value: 'unassign', label: 'Unassign' },
              ]}
            />
          </div>
          {mode === 'inviter' && (
            <Combobox
              value={inviterId}
              onChange={setInviterId}
              options={inviterOptions}
              placeholder="Search outreachers…"
              emptyText="No outreachers found"
              loading={loading}
            />
          )}
          {mode === 'team' && (
            <Combobox
              value={team}
              onChange={setTeam}
              options={teamOptions}
              placeholder="Pick a team…"
              emptyText="No teams found"
              loading={loading}
            />
          )}
          {mode === 'unassign' && (
            <p className="text-sm text-muted-foreground">They go back to the shared queue, open to every outreacher.</p>
          )}
          <CheckboxField
            checked={releaseClaims}
            onChange={setReleaseClaims}
            hint="Clears 'picked up by' so the new assignee can work them straight away."
          >
            Also take it back from whoever claimed it
          </CheckboxField>
          {mutation.error && <Note tone="error">{(mutation.error as Error).message}</Note>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {mutation.isPending ? 'Reassigning…' : `Reassign ${plural(target.count, 'lead')}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
